import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../SEO';
import styleClasses from '../css/mobile/NewMainPage.module.scss';
import { Music, Link2, Sparkles, Check, Download, UserPlus, QrCode, Play, ArrowRight } from 'lucide-react';

import Loading from '../Loading';
import { getDocs, query, collection, where } from 'firebase/firestore';
import { db } from '../../firebase';
import OdometerCounter from '../OdometerCounter';

const MainPage = () => {
  const [musicians, setMusicians] = useState([]);
  const [loading, setLoading] = useState(true);
  // positions 설정
  const positions = {
    'object-left-top': 'left top',
    'object-center-top': 'center top',
    'object-right-top': 'right top',
    'object-left-bottom': 'left bottom',
    'object-center-bottom': 'center bottom',
    'object-right-bottom': 'right bottom',
  };

  useEffect(() => {
    const fetchMusicians = async () => {
      try {
        const q = query(
          collection(db, 'musicians'),
          where('act', '==', true)
        );
        const snap = await getDocs(q);
        const fetched = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setMusicians(fetched);
      } catch (err) {
        console.error('Error fetching musicians data:', err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchMusicians();
  }, []);

  if (loading) return <Loading />;

  return (
    <>
      <SEO title={'Main'} />
        <div className='fullScreen'>
          {/* Hero Section */}
          <section className={styleClasses.heroSection}>
            <div className={styleClasses.heroFloatingBackground}>
              <div className={styleClasses.heroFloatingCirclePurple}></div>
              <div className={styleClasses.heroFloatingCircleViolet}></div>
              <div className={styleClasses.heroFloatingCircleLight}></div>
            </div>

            <div className={styleClasses.heroInner}>
              <div className={styleClasses.heroGrid}>
                {/* Text */}
                <div className={styleClasses.heroTextArea}>
                  <h1 className={styleClasses.heroTitle}>
                    <span className={styleClasses.heroTitleLineRotate}>My Music Profile,</span>
                    <span className={styleClasses.heroTitleLine}>Musicard</span>
                  </h1>
                  <p className={styleClasses.heroSubtitle}>음악하는 당신을 위한 첫 프로필.</p>
                  <div className={styleClasses.heroButtonGroup}>
                    <Link to="/login" className={styleClasses.heroPrimaryButton}>
                      만들기 →
                    </Link>
                    <Link to="/musicard" className={styleClasses.heroSecondaryButton}>
                      둘러보기
                    </Link>
                  </div>
                </div>

                {/* Image */}
                <div className={styleClasses.heroImageArea}>
                  <div className={styleClasses.heroImageWrapper}>
                    <img
                      src="/assets/images/musician-hero.png"
                      alt="Musician Hero"
                      className={styleClasses.heroImage}
                    />
                    <div className={styleClasses.heroProfileInfo}>
                      <div className={styleClasses.heroName}>YUNA</div>
                      <div className={styleClasses.heroRole}>Indie Vocalist • Seoul</div>
                      <div className={styleClasses.heroTags}>
                        <span>Indie</span>
                        <span>Folk</span>
                        <span>Acoustic</span>
                      </div>
                    </div>
                    <div className={styleClasses.heroPlayButton}>
                      <Play size={16} />
                    </div>
                  </div>

                  <div className={styleClasses.heroFloatingCard}>
                    <div className={styleClasses.heroCardUrl}>musizic.com/musicard/yuna</div>
                    <div className={styleClasses.heroSocials}>
                      <span>SoundCloud</span>
                      <span>Instagram</span>
                      <span>YouTube</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section className={styleClasses.featuresSection} id="features">
            <div className={styleClasses.featuresBackground} />

            <div className={styleClasses.featuresInner}>
              <div className={styleClasses.featuresHeader}>
                <h2 className={styleClasses.featuresTitle}>뮤지션을 위한 온라인 명함</h2>
              </div>

              <div className={styleClasses.featuresGrid}>
                <div className={styleClasses.featuresCard}>
                  <div className={styleClasses.featuresIconWrapper}><Music size={32} /></div>
                  <h3 className={styleClasses.featuresCardTitle}>대표곡으로 BGM 재생</h3>
                  <p className={styleClasses.featuresCardDescription}>
                    첫인상은 소리부터 시작되니까. 들려주고픈 음악을 BGM으로.
                  </p>
                </div>

                <div className={styleClasses.featuresCard}>
                  <div className={styleClasses.featuresIconWrapper}><Link2 size={32} /></div>
                  <h3 className={styleClasses.featuresCardTitle}>스트리밍 링크를 한곳에</h3>
                  <p className={styleClasses.featuresCardDescription}>
                    Melon, SoundCloud, Spotify, Instagram, YouTube. 한 곳에 모아.
                  </p>
                </div>

                <div className={styleClasses.featuresCard}>
                  <div className={styleClasses.featuresIconWrapper}><Sparkles size={32} /></div>
                  <h3 className={styleClasses.featuresCardTitle}>나만의 디자인으로</h3>
                  <p className={styleClasses.featuresCardDescription}>
                    내 이야기, 내 색깔, 내 취향을 담은 디자인.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Problem & Solution */}
          <section className={styleClasses.problemSolutionSection}>
            <div className={styleClasses.problemSolutionInner}>
              <div className={styleClasses.problemSolutionGrid}>
                {/* Text Area */}
                <div className={styleClasses.problemSolutionText}>
                  <h2 className={styleClasses.problemSolutionTitle}>
                    <span>복잡할 거 없어.</span>
                    <span className={styleClasses.highlight}>1분이면 충분하니까.</span>
                  </h2>

                  <ul className={styleClasses.problemSolutionChecklist}>
                    <li>
                      <div className={styleClasses.checkIcon}>
                        <Check size={14} />
                      </div>
                      <p>활동명 입력하고</p>
                    </li>
                    <li>
                      <div className={styleClasses.checkIcon}>
                        <Check size={14} />
                      </div>
                      <p>내 앨범 고르고</p>
                    </li>
                    <li>
                      <div className={styleClasses.checkIcon}>
                        <Check size={14} />
                      </div>
                      <p>장르만 선택하면 끝!</p>
                    </li>
                  </ul>
                </div>

                {/* Image Area */}
                <div className={styleClasses.problemSolutionImageWrapper}>
                  <img
                    src="/assets/images/mobile-preview.png"
                    alt="Mobile Preview"
                    className={styleClasses.problemSolutionImage}
                  />
                </div>
              </div>

              {/* Storylane Embed */}
              <div className={styleClasses.storylaneEmbed}>
                <iframe
                  loading="lazy"
                  src="https://app.storylane.io/demo/xfecnpztlura?embed=inline"
                  name="sl-embed"
                  title="How MusiCard Works"
                  allow="fullscreen"
                  className={styleClasses.storylaneIframe}
                ></iframe>
              </div>
            </div>
          </section>

          {/* Portfolio Section */}
          <section className={styleClasses.portfolioSection}>
            <div className={styleClasses.portfolioInner}>
              {/* Section Title */}
              <div className={styleClasses.portfolioTitleWrapper}>
                <h2 className={styleClasses.portfolioTitle}>
                  <span className={styleClasses.grayText}>명함을 넘어서,</span>
                  <span className={styleClasses.purpleText}> 포트폴리오로</span>
                </h2>
                <p className={styleClasses.portfolioSubtitle}>
                다양한 방식으로 활용하는 MusiCard
                </p>
              </div>

              {/* Features Grid */}
              <div className={styleClasses.featuresGrid}>
                <div className={styleClasses.featureCard}>
                  <div className={styleClasses.iconWrapper}>
                    <Download size={32} />
                  </div>
                  <h3 className={styleClasses.featureTitle}>PDF 다운로드</h3>
                  <p className={styleClasses.featureDescription}>
                  머리 아픈 협업 제안서, 프로필 첨부도 간단하게
                  </p>
                </div>

                <div className={styleClasses.featureCard}>
                  <div className={styleClasses.iconWrapper}>
                    <UserPlus size={32} />
                  </div>
                  <h3 className={styleClasses.featureTitle}>협업 제안서</h3>
                  <p className={styleClasses.featureDescription}>
                  SNS 소개 링크, QR 포스터에도 다각도 활용
                  </p>
                </div>

                <div className={styleClasses.featureCard}>
                  <div className={styleClasses.iconWrapper}>
                    <QrCode size={32} />
                  </div>
                  <h3 className={styleClasses.featureTitle}>SNS 소개 링크</h3>
                  <p className={styleClasses.featureDescription}>
                    SNS 소개 링크, QR 포스터에도 활용 가능합니다.
                  </p>
                </div>
              </div>

              {/* Highlight Card */}
              <div className={styleClasses.highlightCard}>
                <p>
                  <span className={styleClasses.purpleText}>뮤지션</span>의 시작은
                  <span className={styleClasses.grayText}> 명함</span>,
                </p>
                <p>
                  도약은 <span className={styleClasses.purpleText}>포트폴리오</span>로.
                </p>
              </div>
            </div>
          </section>

          {/* Gallery Section */}
          <section className={styleClasses.gallerySection} id="gallery">
            <div className={styleClasses.galleryInner}>
              <div className={styleClasses.galleryTitleWrapper}>
                <h2 className={styleClasses.galleryTitle}>Musi Members</h2>
                <div className={styleClasses.gallerySubtitle}><OdometerCounter value={musicians.length} /> 명의 뮤지션이 함께하는 중</div>
              </div>

              <div className={styleClasses.galleryGrid}>
                {musicians.slice(0, 6).map((musician) => (
                  <Link
                    to={`/musicard/${musician.id}`}
                    key={musician.id}
                    className={styleClasses.galleryCard}
                  >
                    <div
                      className={styleClasses.galleryImage}
                      style={{
                        backgroundImage: `url(${musician.profileImage})`,
                        backgroundPosition: positions[musician.position] || 'center',
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                      }}
                    ></div>

                    <div className={styleClasses.overlay}>
                      <h3 className={styleClasses.musicianName}>{musician.name}</h3>
                      <div className={styleClasses.tags}>
                        {musician.genres.slice(0, 6).map((tag, idx) => (
                          <span key={idx} className={styleClasses.tag}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className={styleClasses.playOverlay}>
                      <div className={styleClasses.playButton}>
                        <Play size={24} />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              <div className={styleClasses.viewMoreWrapper}>
                <Link to="/musicard" className={styleClasses.viewMoreButton}>
                  더 많은 뮤지션 보기
                </Link>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className={styleClasses.ctaSection}>
            {/* 배경 효과 */}
            <div className={styleClasses.ctaBackground1}></div>
            <div className={styleClasses.ctaBackground2}></div>
            <div className={styleClasses.ctaOverlay}></div>

            <div className={styleClasses.ctaInner}>
              <h2 className={styleClasses.ctaTitle}>Who's Next?</h2>
              <p className={styleClasses.ctaSubtitle}>뮤지션이라면, 뮤지카드.</p>

              <div className={styleClasses.ctaButtonWrapper}>
                <Link to="/my-page/musicard" className={styleClasses.ctaButton}>
                  뮤지션 등록하기 <ArrowRight size={20} />
                </Link>
              </div>
            </div>
          </section>
        </div>
    </>
  );
};

export default MainPage;